import { useState, useCallback } from 'react';
import { CSVFileEntry, TimeInformationDatabaseData, ZeitschritteData } from '../../../types/common';

interface UseTrainingDataReturn {
    // State
    inputFiles: CSVFileEntry[];
    outputFiles: CSVFileEntry[];
    timeInfo: TimeInformationDatabaseData;
    zeitschritte: ZeitschritteData;
    isUploading: boolean;
    resetKey: number;

    // Setters
    setInputFiles: React.Dispatch<React.SetStateAction<CSVFileEntry[]>>;
    setOutputFiles: React.Dispatch<React.SetStateAction<CSVFileEntry[]>>;
    setTimeInfo: React.Dispatch<React.SetStateAction<TimeInformationDatabaseData>>;
    setZeitschritte: React.Dispatch<React.SetStateAction<ZeitschritteData>>;
    setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;

    // Actions
    handleInputFilesChange: (files: CSVFileEntry[]) => void;
    handleOutputFilesChange: (files: CSVFileEntry[]) => void;
    handleTimeInfoChange: (data: TimeInformationDatabaseData) => void;
    handleZeitschritteChange: (data: ZeitschritteData) => void;
    resetTrainingData: () => void;
    incrementResetKey: () => void;
}

const initialTimeInfo: TimeInformationDatabaseData = {
    session_id: '',
    jahr: false,
    woche: false,
    monat: false,
    feiertag: false,
    tag: false,
    zeitzone: '',
    category_data: {}
};

const initialZeitschritte: ZeitschritteData = {
    eingabe: '',
    ausgabe: '',
    zeitschrittweite: '',
    offset: ''
};

export const useTrainingData = (): UseTrainingDataReturn => {
    const [inputFiles, setInputFiles] = useState<CSVFileEntry[]>([]);
    const [outputFiles, setOutputFiles] = useState<CSVFileEntry[]>([]);
    const [timeInfo, setTimeInfo] = useState<TimeInformationDatabaseData>(initialTimeInfo);
    const [zeitschritte, setZeitschritte] = useState<ZeitschritteData>(initialZeitschritte);
    const [isUploading, setIsUploading] = useState(false);
    const [resetKey, setResetKey] = useState<number>(0);

    const handleInputFilesChange = useCallback((files: CSVFileEntry[]) => {
        setInputFiles(files);
    }, []);

    const handleOutputFilesChange = useCallback((files: CSVFileEntry[]) => {
        setOutputFiles(files);
    }, []);

    const handleTimeInfoChange = useCallback((data: TimeInformationDatabaseData) => {
        setTimeInfo(data);
    }, []);

    const handleZeitschritteChange = useCallback((data: ZeitschritteData) => {
        setZeitschritte(data);
    }, []);

    const resetTrainingData = useCallback(() => {
        setInputFiles([]);
        setOutputFiles([]);
        setTimeInfo(initialTimeInfo);
        setZeitschritte(initialZeitschritte);
        setIsUploading(false);
    }, []);

    const incrementResetKey = useCallback(() => {
        setResetKey(prev => prev + 1);
    }, []);

    return {
        // State
        inputFiles,
        outputFiles,
        timeInfo,
        zeitschritte,
        isUploading,
        resetKey,

        // Setters
        setInputFiles,
        setOutputFiles,
        setTimeInfo,
        setZeitschritte,
        setIsUploading,

        // Actions
        handleInputFilesChange,
        handleOutputFilesChange,
        handleTimeInfoChange,
        handleZeitschritteChange,
        resetTrainingData,
        incrementResetKey
    };
};
